import type { ThirdPartySensorRepository } from "~application/devices/ports/third-party-sensor-repository.ts";
import type { Modulator } from "~domain/devices/models/modulators/modulator-model.ts";
import type { ThirdPartyAppliance } from "~domain/devices/models/third-party/third-party-appliance-model.ts";
import type { BackofficeFindThirdPartyAppliancesResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-third-party-appliances-response.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";

import {
  DomainError,
  DomainErrorCode,
  DomainInvalidArgumentError,
  DomainNotFoundError,
  DomainTransportError,
  DomainUnauthorizedError,
} from "~domain/global/errors/domain-error.ts";

function mapBackofficeThirdPartySensorAppliancesToDomain(
  backofficeAppliances: BackofficeFindThirdPartyAppliancesResponse,
): Array<ThirdPartyAppliance> {
  return backofficeAppliances.appliances.map((app) => ({
    name: app.name ?? undefined,
    modulatorCsId: app.csId ?? undefined,
    modulatorHwId: app.hwId,
  }));
}

export class BackofficeThirdPartySensorRepository implements ThirdPartySensorRepository {
  constructor(private readonly backofficeApi: BackofficeApi) {}

  async findLinkedAppliances(thirdPartySensor: Modulator): Promise<Array<ThirdPartyAppliance>> {
    if (!thirdPartySensor.csId) {
      throw new DomainInvalidArgumentError(`Third party sensor should have its cs id setted`);
    }

    const response = await this.backofficeApi.devices.findThirdPartySensorAppliances({
      params: {
        thirdPartySensorCsId: thirdPartySensor.csId,
      },
    });

    if (response.status === 200) {
      return mapBackofficeThirdPartySensorAppliancesToDomain(response.body);
    }

    if (response.status === 404) {
      throw new DomainNotFoundError(`Unable to found appliances for third party sensor cs id ${thirdPartySensor.csId}`);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async removeLinkedAppliance(thirdPartySensor: Modulator, appliance: ThirdPartyAppliance): Promise<void> {
    if (!thirdPartySensor.csId) {
      throw new DomainInvalidArgumentError(`Third party sensor ${thirdPartySensor.hwId} should have its cs id setted`);
    }

    if (!appliance.modulatorCsId) {
      throw new DomainInvalidArgumentError(
        `Third party sensor's appliance ${appliance.modulatorHwId} should have its cs id setted`,
      );
    }

    const response = await this.backofficeApi.devices.deleteThirdPartyAssociation({
      params: {
        thirdPartySensorCsId: thirdPartySensor.csId,
        applianceCsId: appliance.modulatorCsId,
      },
    });

    if (response.status === 200) {
      return;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }
}
